import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { 
  Users, UserPlus, Search, Building2, User, Heart, TrendingUp, 
  Phone, Mail, MapPin, Star, Eye, Edit, AlertCircle, CheckCircle,
  DollarSign, Calendar, RefreshCw, Filter, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const TIPO_DOADOR_COLORS = {
  "Empresa": "bg-blue-100 text-blue-800",
  "Fundação": "bg-purple-100 text-purple-800",
  "Individual": "bg-green-100 text-green-800",
  "Instituição Pública": "bg-orange-100 text-orange-800",
  "Associação": "bg-pink-100 text-pink-800",
  "Outro": "bg-gray-100 text-gray-800"
};

const STATUS_COLORS = {
  "Ativo": "bg-green-500",
  "Inativo": "bg-gray-500",
  "Pausado": "bg-yellow-500",
  "Bloqueado": "bg-red-500"
};

export default function DoadoresListPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [doadores, setDoadores] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [tipos, setTipos] = useState(null);
  
  // Filtros
  const [filtros, setFiltros] = useState({
    search: '',
    tipo_doador: '',
    status: '',
    disponibilidade_urgente: false
  });
  
  // Modal criar/editar
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDoador, setEditingDoador] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    email_principal: '',
    tipo_doador: 'Individual',
    telefone: '',
    telemovel: '',
    nif: '',
    morada: '',
    distrito_nome: '',
    disponibilidade_urgente: false,
    tipos_recursos_interesse: [],
    newsletter: true,
    rgpd_consentimento: true,
    notas_internas: ''
  });

  useEffect(() => {
    carregarDados();
    carregarTipos();
    carregarEstatisticas();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtros.search) params.append('search', filtros.search);
      if (filtros.tipo_doador && filtros.tipo_doador !== 'all') params.append('tipo_doador', filtros.tipo_doador);
      if (filtros.status && filtros.status !== 'all') params.append('status', filtros.status);
      if (filtros.disponibilidade_urgente) params.append('disponibilidade_urgente', 'true');
      
      const res = await apiClient.get(`/doadores?${params.toString()}`);
      setDoadores(res.data.doadores || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      toast.error('Erro ao carregar doadores');
    } finally {
      setLoading(false);
    }
  };

  const carregarTipos = async () => {
    try {
      const res = await apiClient.get('/doadores/tipos');
      setTipos(res.data);
    } catch (error) {
      console.error('Erro ao carregar tipos:', error);
    }
  };

  const carregarEstatisticas = async () => {
    try {
      const res = await apiClient.get('/doadores/estatisticas');
      setStats(res.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const handleSearch = () => {
    carregarDados();
  };

  const handleNovoDoador = () => {
    setEditingDoador(null);
    setFormData({
      nome: '',
      email_principal: '',
      tipo_doador: 'Individual',
      telefone: '',
      telemovel: '',
      nif: '',
      morada: '',
      distrito_nome: '',
      disponibilidade_urgente: false,
      tipos_recursos_interesse: [],
      newsletter: true,
      rgpd_consentimento: true,
      notas_internas: ''
    });
    setModalOpen(true);
  };

  const handleEditDoador = (doador) => {
    setEditingDoador(doador);
    setFormData({
      nome: doador.nome,
      email_principal: doador.email_principal,
      tipo_doador: doador.tipo_doador,
      telefone: doador.telefone || '',
      telemovel: doador.telemovel || '',
      nif: doador.nif || '',
      morada: doador.morada || '',
      distrito_nome: doador.distrito_nome || '',
      disponibilidade_urgente: doador.disponibilidade_urgente,
      tipos_recursos_interesse: doador.tipos_recursos_interesse || [],
      newsletter: doador.newsletter,
      rgpd_consentimento: doador.rgpd_consentimento,
      notas_internas: doador.notas_internas || ''
    });
    setModalOpen(true);
  };

  const handleSaveDoador = async () => {
    try {
      const fd = new FormData();
      fd.append('nome', formData.nome);
      fd.append('email_principal', formData.email_principal);
      fd.append('tipo_doador', formData.tipo_doador);
      if (formData.telefone) fd.append('telefone', formData.telefone);
      if (formData.telemovel) fd.append('telemovel', formData.telemovel);
      if (formData.nif) fd.append('nif', formData.nif);
      if (formData.morada) fd.append('morada', formData.morada);
      if (formData.distrito_nome) fd.append('distrito_nome', formData.distrito_nome);
      fd.append('disponibilidade_urgente', formData.disponibilidade_urgente);
      fd.append('tipos_recursos_interesse', JSON.stringify(formData.tipos_recursos_interesse));
      fd.append('newsletter', formData.newsletter);
      fd.append('rgpd_consentimento', formData.rgpd_consentimento);
      if (formData.notas_internas) fd.append('notas_internas', formData.notas_internas);
      
      if (editingDoador) {
        await apiClient.put(`/doadores/${editingDoador.id}`, fd);
        toast.success('Doador atualizado com sucesso');
      } else {
        await apiClient.post('/doadores', fd);
        toast.success('Doador criado com sucesso');
      }
      
      setModalOpen(false);
      carregarDados();
      carregarEstatisticas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar doador');
    }
  };

  const handleVerDetalhes = (doadorId) => {
    navigate(`/doadores/${doadorId}`);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatCurrency = (value) => {
    if (!value) return '€0';
    return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(value);
  };

  return (
    <div className="space-y-6" data-testid="doadores-list-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Heart className="w-8 h-8 text-red-500" />
            Gestão de Doadores
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Sistema completo de gestão e contacto de doadores
          </p>
        </div>
        <Button onClick={handleNovoDoador} className="bg-red-600 hover:bg-red-700" data-testid="btn-novo-doador">
          <UserPlus className="w-4 h-4 mr-2" />
          Novo Doador
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Doadores</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Ativos</p>
                  <p className="text-2xl font-bold text-green-600">{stats.ativos}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Doado</p>
                  <p className="text-2xl font-bold text-purple-600">{formatCurrency(stats.valor_total_doado)}</p>
                </div>
                <DollarSign className="w-8 h-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Disponíveis Urgências</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.com_disponibilidade_urgente}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Score Médio</p>
                  <p className={`text-2xl font-bold ${getScoreColor(stats.score_responsividade_medio)}`}>
                    {stats.score_responsividade_medio}%
                  </p>
                </div>
                <Star className="w-8 h-8 text-yellow-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-4 items-end flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Label>Pesquisar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Nome, email ou NIF..."
                  value={filtros.search}
                  onChange={e => setFiltros(p => ({ ...p, search: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-48">
              <Label>Tipo</Label>
              <Select 
                value={filtros.tipo_doador} 
                onValueChange={v => setFiltros(p => ({ ...p, tipo_doador: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos os tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {tipos?.tipos_doador?.map(tipo => (
                    <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label>Status</Label>
              <Select 
                value={filtros.status} 
                onValueChange={v => setFiltros(p => ({ ...p, status: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="Ativo">Ativo</SelectItem>
                  <SelectItem value="Inativo">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="urgente"
                checked={filtros.disponibilidade_urgente}
                onCheckedChange={v => setFiltros(p => ({ ...p, disponibilidade_urgente: v }))}
              />
              <Label htmlFor="urgente" className="text-sm cursor-pointer">Só urgências</Label>
            </div>
            <Button onClick={handleSearch} disabled={loading}>
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Filter className="w-4 h-4 mr-2" />}
              Filtrar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Lista de Doadores ({total})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Doador</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Contacto</TableHead>
                <TableHead className="text-center">Doações</TableHead>
                <TableHead className="text-center">Total Doado</TableHead>
                <TableHead className="text-center">Score</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {doadores.map((doador) => (
                <TableRow key={doador.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        doador.tipo_doador === 'Empresa' ? 'bg-blue-100' :
                        doador.tipo_doador === 'Individual' ? 'bg-green-100' : 'bg-purple-100'
                      }`}>
                        {doador.tipo_doador === 'Empresa' ? 
                          <Building2 className="w-5 h-5 text-blue-600" /> :
                          <User className="w-5 h-5 text-green-600" />
                        }
                      </div>
                      <div>
                        <p className="font-medium">{doador.nome}</p>
                        {doador.distrito_nome && (
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {doador.distrito_nome}
                          </p>
                        )}
                      </div>
                      {doador.disponibilidade_urgente && (
                        <Badge className="bg-orange-500 text-white text-xs">Urgências</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={TIPO_DOADOR_COLORS[doador.tipo_doador]}>
                      {doador.tipo_doador}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1 text-sm">
                      <p className="flex items-center gap-1">
                        <Mail className="w-3 h-3 text-slate-400" />
                        {doador.email_principal}
                      </p>
                      {doador.telefone && (
                        <p className="flex items-center gap-1">
                          <Phone className="w-3 h-3 text-slate-400" />
                          {doador.telefone}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="font-medium">{doador.total_doacoes || 0}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="font-medium text-purple-600">
                      {formatCurrency(doador.valor_total_doado)}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={`font-bold text-lg ${getScoreColor(doador.score_responsividade)}`}>
                      {doador.score_responsividade || 50}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${STATUS_COLORS[doador.status]}`} />
                      <span className="text-sm">{doador.status}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleVerDetalhes(doador.id)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleEditDoador(doador)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {doadores.length === 0 && !loading && (
            <div className="text-center py-12 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Nenhum doador encontrado</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Criar/Editar */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingDoador ? 'Editar Doador' : 'Novo Doador'}
            </DialogTitle>
            <DialogDescription>
              {editingDoador ? 'Atualize os dados do doador' : 'Preencha os dados para registar um novo doador'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nome *</Label>
                <Input
                  value={formData.nome}
                  onChange={e => setFormData(p => ({ ...p, nome: e.target.value }))}
                  placeholder="Nome completo ou empresa"
                />
              </div>
              <div>
                <Label>Tipo de Doador *</Label>
                <Select 
                  value={formData.tipo_doador} 
                  onValueChange={v => setFormData(p => ({ ...p, tipo_doador: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tipos?.tipos_doador?.map(tipo => (
                      <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email_principal}
                  onChange={e => setFormData(p => ({ ...p, email_principal: e.target.value }))}
                  placeholder="email@exemplo.pt"
                />
              </div>
              <div>
                <Label>NIF</Label>
                <Input
                  value={formData.nif}
                  onChange={e => setFormData(p => ({ ...p, nif: e.target.value }))}
                  placeholder="NIF"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Telefone</Label>
                <Input
                  value={formData.telefone}
                  onChange={e => setFormData(p => ({ ...p, telefone: e.target.value }))}
                  placeholder="+351 xxx xxx xxx"
                />
              </div>
              <div>
                <Label>Telemóvel</Label>
                <Input
                  value={formData.telemovel}
                  onChange={e => setFormData(p => ({ ...p, telemovel: e.target.value }))}
                  placeholder="+351 9xx xxx xxx"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Morada</Label>
                <Input
                  value={formData.morada}
                  onChange={e => setFormData(p => ({ ...p, morada: e.target.value }))}
                  placeholder="Morada completa"
                />
              </div>
              <div>
                <Label>Distrito</Label>
                <Input
                  value={formData.distrito_nome}
                  onChange={e => setFormData(p => ({ ...p, distrito_nome: e.target.value }))}
                  placeholder="Distrito"
                />
              </div>
            </div>
            
            <div>
              <Label>Notas Internas</Label>
              <Textarea
                value={formData.notas_internas}
                onChange={e => setFormData(p => ({ ...p, notas_internas: e.target.value }))}
                placeholder="Observações internas sobre o doador..."
                rows={3}
              />
            </div>
            
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="disponibilidade"
                  checked={formData.disponibilidade_urgente}
                  onCheckedChange={v => setFormData(p => ({ ...p, disponibilidade_urgente: v }))}
                />
                <Label htmlFor="disponibilidade" className="cursor-pointer">
                  Disponível para urgências
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="newsletter"
                  checked={formData.newsletter}
                  onCheckedChange={v => setFormData(p => ({ ...p, newsletter: v }))}
                />
                <Label htmlFor="newsletter" className="cursor-pointer">
                  Receber newsletter
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="rgpd"
                  checked={formData.rgpd_consentimento}
                  onCheckedChange={v => setFormData(p => ({ ...p, rgpd_consentimento: v }))}
                />
                <Label htmlFor="rgpd" className="cursor-pointer">
                  Consentimento RGPD
                </Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveDoador}
              disabled={!formData.nome || !formData.email_principal}
              className="bg-red-600 hover:bg-red-700"
            >
              {editingDoador ? 'Atualizar' : 'Criar'} Doador
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
